import { Expose } from "class-transformer";
import { TicketsStatusChange } from "./ticket-status-change";
import { CreateTicketsInput, States, UpdateTicketsInput } from "@generated/queries";
import { Site } from "./tkt-site";
import { TicketLabor } from "./ticket-labor";
import { TicketMaterial } from "./ticket-material";
import { TicketSchedule } from "./ticket-schedule";
import { TicketAreaMap } from "./ticket-area-map";
import { User } from "@core/model/user/user";
import { BusLookup } from "@core/model/bus-lookup/bus-lookup";
import { TicketLocationMap } from "./ticket-location-map";
import { Attachment } from "@core/model/attachment";
import { Notes } from "@core/model/note/note";

export class Ticket {
    @Expose() id: string | undefined;
    @Expose() businessId: string | undefined;
    @Expose() ticketNo: string | undefined;
    @Expose() siteId: string | undefined;
    @Expose() facilityId: string | undefined;
    @Expose() areaId: string | undefined;
    @Expose() reportedUser: User | undefined;
    @Expose() reportedById: string | undefined;
    @Expose() entryDate: Date | undefined;
    @Expose() scheduleDate: Date | undefined;
    @Expose() dueDate: Date | undefined;
    @Expose() completeDate: Date | undefined;
    @Expose() firstResponseDate: Date | undefined;
    @Expose() executionStartDate: Date | undefined;
    @Expose() executionEndDate: Date | undefined;
    @Expose() completionDuration: AppTime | undefined;
    @Expose() problemId: string | undefined;
    @Expose() priorityId: string | undefined;
    @Expose() ticketStatusChanges: TicketsStatusChange[] | undefined;
    @Expose() problemLookup: BusLookup | undefined;
    @Expose() priorityLookup: BusLookup | undefined;
    @Expose() resolutionId: string | undefined;
    @Expose() resolutionLookup: BusLookup | undefined;
    @Expose() site: Site | undefined;
    @Expose() facility: Site | undefined;
    @Expose() area: Site | undefined;
    @Expose() ticketLabor: TicketLabor[] | undefined;
    @Expose() ticketMaterial: TicketMaterial[] | undefined;
    @Expose() currentChangeStatusId: string | undefined;
    @Expose() estimatedTime: number | undefined;
    @Expose() planStartDate: Date | undefined;
    @Expose() planEndDate: Date | undefined;
    @Expose() ticketScheduleId: string | undefined;
    @Expose() ticketTemplateSchedule: TicketSchedule | undefined;
    @Expose() ticketNumber: number | undefined;
    @Expose() ticketYear: number | undefined;
    @Expose() isScheduled: boolean | undefined;
    @Expose() ticketCountDisplayOrder: number|undefined;
    @Expose() ticketLocationMap: TicketLocationMap[] | undefined;
    @Expose() ticketAreaMap: TicketAreaMap[] | undefined;
    @Expose() ticketBookmark: boolean | undefined;
    @Expose() latestStatusChange: LatestStatusChange | undefined;
    @Expose() firstStatusChange: FirstStatusChange | undefined;
    @Expose() ticketNoteWithAttachments: FirstNote | undefined;
    @Expose() createdOn:string|undefined;
    @Expose() updatedOn:Date|undefined;
    dueTicket: string | undefined;
  }


  export class UpdateLocation{
    @Expose() ticketId:string;
    @Expose() locationIds:string[];
    @Expose() areaIds:string[];
  }

  export class LatestStatusChange{
    @Expose() id: string | undefined;
    @Expose() assignedUserId: string | undefined;
    @Expose() changeStatusId: string | undefined;
  }

  export class FirstStatusChange{
    @Expose() id: string | undefined;
    @Expose() assignedUserId: string | undefined;
    @Expose() changeStatusId: string | undefined;
  }

  export class FirstNote{
    @Expose() note: Notes | undefined;
    @Expose() attachments: Attachment[] | undefined;
  }

  export class UpdateTicket implements UpdateTicketsInput  {
    @Expose() id: string|undefined;
    @Expose() reportedById: string | undefined;
    @Expose() state: States | undefined;
    @Expose() businessId: string | undefined;
    @Expose() ticketNo: string | undefined;
    @Expose() siteId: string | undefined;
    @Expose() facilityId: string | undefined;
    @Expose() areaId: string | undefined;
    @Expose() reportedBy: string | undefined;
    @Expose() entryDate: Date | undefined;
    @Expose() scheduleDate: Date | undefined;
    @Expose() dueDate: Date | undefined;
    @Expose() completeDate: Date | undefined;
    @Expose() firstResponseDate: Date | undefined;
    @Expose() executionStartDate: Date | undefined;
    @Expose() executionEndDate: Date | undefined;
    @Expose() completionDuration: AppTime | undefined;
    @Expose() problemId: string | undefined;
    @Expose() priorityId: string | undefined;
    @Expose() ticketStatusChanges: TicketsStatusChange[] | undefined;
    @Expose() estimatedTime: number | undefined;
    @Expose() planStartDate: Date | undefined;
    @Expose() planEndDate: Date | undefined;
  }

  export class UpdateTicketPlanDateRange {
     @Expose() id: string; // Guid as string
     @Expose() planStartDate: string; // optional
     @Expose() planEndDate: string;   // optional
     @Expose() updatedOn: string|undefined;
  }

  export class UpdateTicketDueDate  {
    @Expose() id: string|undefined;
    @Expose() businessId: string | undefined;
    @Expose() dueDate: Date | undefined;
    @Expose() updatedOn: string | undefined;
}
  export class UpdateTicketProblem  {
    @Expose() id: string|undefined;
    @Expose() problemId: string | undefined;
    @Expose() businessId: string | undefined;
}

  export class CreateTicket implements CreateTicketsInput {
    @Expose() reportedById: string | undefined;
    @Expose() state: States | undefined;
    @Expose() businessId: string | undefined;
    @Expose() ticketNo: string | undefined;
    @Expose() siteId: string | undefined;
    @Expose() facilityId: string | undefined;
    @Expose() areaId: string | undefined;
    @Expose() reportedBy: string | undefined;
    @Expose() entryDate: Date | undefined;
    @Expose() scheduleDate: Date | undefined;
    @Expose() dueDate: Date | undefined;
    @Expose() completeDate: Date | undefined;
    @Expose() firstResponseDate: Date | undefined;
    @Expose() executionStartDate: Date | undefined;
    @Expose() executionEndDate: Date | undefined;
    @Expose() completionDuration: AppTime | undefined;
    @Expose() problemId: string | undefined;
    @Expose() priorityId: string | undefined;
    @Expose() ticketStatusChanges: TicketsStatusChange[] | undefined;
    @Expose() ticketLocationMap: TicketLocationMap[] | undefined;
    @Expose() ticketAreaMap: TicketAreaMap[] | undefined;
    @Expose() createdOn: string|undefined
  }

  export class UpdateTicketBookMark {
    @Expose() id: string | undefined;
    @Expose() ticketBookmark: boolean | undefined;
    @Expose() updatedOn: string | undefined;
  }

  export class UpdateTicketPriority {
    @Expose() id: string | undefined;
    @Expose() businessId: string | undefined;
    @Expose() priorityId: string | undefined;
    @Expose() updatedOn: string | undefined;
  }

  export class TicketOrderUpdateInput {
    id: string;   // UUID as string
    order: number;
  }

  export class UpdateTicketOrder {
    tickets: TicketOrderUpdateInput[];
  }



export interface AppTime {
  hours: number;
  minutes: number;
}

export class TicketFilters {
  facilityId: string | undefined;
  siteId: string | undefined;
  areaId: string | undefined;
  priorityId: string | undefined;
  problemId: string | undefined;
  managerId: string | undefined;
  reportedById: string | undefined;
  workerId: string | undefined;
  statusIds: string[] | undefined;
  ticketType: string | undefined;
  ticketNo: string | undefined;
  fromEntryDate: Date | undefined;
  toEntryDate: Date | undefined;
  fromDueDate: Date | undefined;
  toDueDate: Date | undefined;
  planStartDate: Date | undefined;
  planEndDate: Date | undefined;
  isBookMark: boolean | undefined;
  ticketScheduleId: string | undefined;
}
